import React, { useEffect } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Input } from '@components/form/input';
import { InputPhone } from '@components/form/input/inputPhone';
import { SelectForm, SelectDate } from '@components/form/select';
import { HrPrimary, HrRed, Separate } from '@components/styles/hr-styles';
import { useMutation } from '@tanstack/react-query';
import useStore from '../store/useStore';
import { typesDocuments, typesPhone } from '../utils/constants';
import { allowedDomains } from '../../constant/allowed-domains';

import { useQuery } from '../../hook/use-query';

import { _gender } from '../../constant/vacancy';
import { vacancyRequirementType } from '../../constant/vacancy-requirement';
import { getCountries, getCountryLocalities, getProvinces } from '../../crud_vacancy/services/api-hiringroom';
import { isPostulanExists } from '../services/api';
import { DisabledPerson } from './disabled/disabled';
import { ScreenLoadImportCV } from './load/import-cv';
import { PersonalExpectations } from './personal-expectations/personal-expectations';
import { TermsConditions } from './terms-and-conditions/terms-and-conditions';
import { UploadImage } from './upload-image';
import { phoneCountryCodes } from '../../components/utilities/utilities';

const Container = styled.div`
  display: flex;
  width: 100%;
  gap: 24px;
  padding: 0 32px;
  box-sizing: border-box;
  @media (max-width: 768px) {
    flex-wrap: wrap;
    grid-template-columns: 1fr;
    padding: 0 16px;
  }
`;
const SubContainer = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr 1fr;
  width: 100%;

  & .twoColumns {
    grid-column: 1/-1;

    & div {
      padding: 0;

      & h4 {
        padding-bottom: 8px;
      }

      & h5 {
        margin: 0;
      }
    }
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const StepPersonalInformation = observer(
  ({
    register,
    unregister,
    formState,
    control,
    postulant,
    getValues,
    setValue,
    loading,
    setError,
    clearErrors,
    show,
    action,
    setShowModalUpdateProfile,
    trigger
  }) => {
    const _address = postulant.locality?.split(',') || [];
    const _provinceName = _address[_address.length - 2] || '';
    const _localityName = _address[_address.length - 3] || '';
    const [t] = useTranslation();
    const _queryParams = useQuery();
    const {
      vacancyRequirement,
      setCountries,
      setProvinces,
      setLocalities,
      filterProvincies,
      filterLocalites,
      countries,
      provinces,
      localities,
      getDataPostulant,
      vacancy,
      questions
    } = useStore();
    const { mandatory, disabled } = vacancyRequirementType;
    const {
      address,
      born,
      dni,
      url_perfil: urlPerfil,
      firstname,
      gender,
      lastname,
      locality,
      mail,
      nationality,
      phone,
      phone2,
      language,
      legacy,
      relocation,
      jobs,
      known,
      remuneration,
      salario,
      school,
      educ,
      handicapped,
      vacancy_id: vacancyId,
      attached_file: attachedFile,
      available_time: availableTime,
      social_networks: socialNetworks
    } = vacancyRequirement;
    const arrRequeriment = [
      language,
      legacy,
      relocation,
      jobs,
      known,
      remuneration,
      salario,
      school,
      educ,
      vacancyId,
      attachedFile,
      availableTime,
      socialNetworks
    ].some((element) => element === 1 || element === 2);
    const hasQuestions = questions.length !== 0;

    const _getCountries = useMutation(getCountries, {
      onSuccess: (data) => setCountries(data.data.response),
      onError: (error) => console.log(error)
    });

    const _getProvinces = useMutation(getProvinces, {
      onSuccess: (data) => setProvinces(data.data.response),
      onError: (error) => console.log(error)
    });

    const _getCountryLocalities = useMutation(getCountryLocalities, {
      onSuccess: (data) => setLocalities(data.data.response),
      onError: (error) => console.log(error)
    });

    const postulanExists = useMutation(isPostulanExists, {
      onSuccess: (data) => {
        if (data.data.response.exists) {
          setError('email', {
            message: (
              <span style={{ alignSelf: 'center', fontSize: '12px', color: HrRed }}>
                {t('postulant.error.postulant_exist_req.title1')}{' '}
                <span
                  className='postulation-update-exists'
                  style={{ color: HrPrimary, cursor: 'pointer' }}
                  onClick={() => setShowModalUpdateProfile(true)}
                  onKeyPress={() => setShowModalUpdateProfile(true)}
                  role='button'
                  tabIndex='0'
                >
                  {t('postulant.error.postulant_exist_req.title2')}
                </span>
              </span>
            )
          });
        }
      }
    });

    const _genderCustom = _gender.map((g) => ({
      ...g,
      value: g.value !== null ? g.value : 'other'
    }));

    const sendData = async (email) => {
      const nameEmail = 'email';
      const isValidMail = await trigger(nameEmail);
      const referralId = _queryParams.get('ref');
      const source = _queryParams.get('source');
      const dataToSend = {
        vacancy_id: vacancy._id.$oid,
        email,
        account: vacancy.account,
        referralId,
        source
      };
      if (isValidMail) postulanExists.mutate(dataToSend);
    };

    const isInWhiteList = (emailDomain) => {
      const allowedDomainsLowerCase = allowedDomains.map((domain) => domain.toLowerCase());
      const validHotmailRegex = new RegExp('^hotmail(\\.com)?(\\.[a-z]{2})?$');
      return allowedDomainsLowerCase.includes(emailDomain) || validHotmailRegex.test(emailDomain);
    };

    const isAnInvalidDomain = (emailDomain) => {
      // La idea es sacar estas expresiones a un archivos de constantes, guardarlas en un array y poder recorrer el mismo para correr los tests

      const badDomainGmail = new RegExp('^gma[a-z]?l.[a-z.]+$');
      const badDomainGmail2 = new RegExp('^gmail[a-z]+.[a-z.]+$');
      const badDomainHotmail = new RegExp('^ho[tm]{1,2}[ai]{1,2}l?.[a-z.]{2,}$');

      const match1 = badDomainGmail.test(emailDomain);
      const match2 = badDomainGmail2.test(emailDomain);
      const match3 = badDomainHotmail.test(emailDomain);

      return match1 || match2 || match3;
    };

    const validateEmail = (value) => {
      // validar que tenga forma de email => ya lo hace la lib => no lo vamos a hacer
      // validar que el email no exista en la vacante => tampoco lo vamos a hacer aqui
      // evaluar el dominio de la direccion de email para saber si fue mal tipeado o no

      const emailConfirm = getValues('emailConfirm');
      const email = value;
      const emailSplit = email.split('@');
      const emailDomain = emailSplit[1];

      if (emailConfirm === '') return true;
      if (email !== emailConfirm) return t('postulant.error.invalidEmail');
      if (isInWhiteList(emailDomain)) return true;
      if (isAnInvalidDomain(emailDomain)) return t('postulant.error.invalidEmailDomain');

      clearErrors(['email']);
      return true;
    };

    const sameEmail = () => {
      const email = getValues('email');
      const emailConfirm = getValues('emailConfirm');

      if (emailConfirm !== '' && email !== emailConfirm) {
        setError('email', { message: t('postulant.error.invalidEmail') });
        return;
      }

      clearErrors(['email']);
    };

    const emailOnBlur = (inputId, event) => {
      const emailLowerCase = event.target.value.toLowerCase();
      setValue(inputId, emailLowerCase);
      if (inputId === 'emailConfirm') sameEmail();
      if (inputId === 'email') sendData(emailLowerCase);
    };

    const notPaste = (e) => {
      e.preventDefault();
      return false;
    };

    useEffect(() => {
      if (countries.length === 0) _getCountries.mutate();
      if (provinces.length === 0) _getProvinces.mutate();
      if (localities.length === 0) _getCountryLocalities.mutate();
    }, []);

    useEffect(() => {
      if (countries.length > 0 && postulant.location) {
        setValue('location.country', postulant.location.country);
        filterProvincies(postulant.location?.country);
      }
    }, [countries, postulant.location]);

    useEffect(() => {
      if (countries.length > 0 && postulant.nationality) {
        const allCountries = JSON.parse(JSON.stringify(countries));
        const country = allCountries.filter(
          (c) => c.value === postulant.nationality || c.label === postulant.nationality
        );

        if (country && country[0]) {
          setValue('nationality', country[0].value);
        }
      }
    }, [countries, postulant.nationality]);

    useEffect(() => {
      if (provinces.length > 0 && postulant.location?.province) {
        const pro = provinces.find((p) => {
          return p.value === postulant.location?.province;
        });

        if (pro) {
          setValue('location.province', postulant.location.province);
        }
        filterLocalites(postulant.location.province);
      } else if (provinces.length > 0 && postulant.locality) {
        const pro = provinces.find((p) => {
          return p.label === _provinceName.trim();
        });
        if (pro) {
          setValue('location.province', pro.value);
          filterLocalites(pro.value);
        } else {
          filterLocalites(null);
          setValue('location.province', null);
        }
      } else {
        filterLocalites(null);
        unregister('location.province');
        unregister('location.city');
      }
    }, [provinces, postulant.locality]);

    useEffect(() => {
      if (localities.length > 0 && postulant.location?.city) {
        const pro = localities.find((p) => {
          return p.value === postulant.location?.city;
        });

        if (pro) {
          setValue('location.city', postulant.location.city);
        }
      } else if (localities.length > 0 && postulant.locality) {
        const city = localities.find((p) => {
          return p.label === _localityName.trim();
        });
        if (city) {
          setValue('location.city', city.value);
        }
      }

      if (localities.length === 1) {
        setValue('location.city', localities[0]?.value);
      }
    }, [localities, postulant.locality]);

    if (loading) return <ScreenLoadImportCV />;

    let prefixPhoneDefaultCountryCode = null;
    if (phone !== disabled) {
      prefixPhoneDefaultCountryCode = postulant.prefix_phone_1
        ? postulant.prefix_phone_1
        : phoneCountryCodes(getDataPostulant.accountCountry).toString();
    }

    let prefixPhoneSecondaryCountryCode = null;
    if (phone2 !== disabled) {
      prefixPhoneSecondaryCountryCode = postulant.prefix_phone_2
        ? postulant.prefix_phone_2
        : phoneCountryCodes(getDataPostulant.accountCountry).toString();
    }

    return (
      <Container style={{ display: show ? '' : 'none' }} isHideUrlPerfil={urlPerfil === disabled}>
        {urlPerfil !== disabled && (
          <UploadImage
            control={control}
            validate={{
              required: urlPerfil === mandatory,
              size: true,
              format: true
            }}
            fotoUrl={postulant.fotoURL}
            formState={formState}
            trigger={trigger}
            name='foto_perfil'
            setValue={setValue}
          />
        )}
        <SubContainer>
          {/* NOMBRE DEL POSTULANTE */}
          {firstname !== disabled && (
            <Input
              title={t(`postulant.firstname`)}
              placeholder={t(`postulant.placeholder.firstname`)}
              name='firstname'
              type='text'
              register={register}
              formState={formState}
              validate={{
                required: firstname === mandatory,
                caracterNoNumerics: true
              }}
              errorPosition='bottom'
              handlerOnBlur={(e) => {
                setValue('firstname', e.target.value);
              }}
            />
          )}
          {/* APELLIDO DEL POSTULANTE */}
          {lastname !== disabled && (
            <Input
              title={t(`postulant.lastname`)}
              placeholder={t(`postulant.placeholder.lastname`)}
              name='lastname'
              type='text'
              register={register}
              formState={formState}
              validate={{
                required: lastname === mandatory,
                whiteSpace: false,
                caracterNoNumerics: true
              }}
              errorPosition='bottom'
              handlerOnBlur={(e) => {
                setValue('lastname', e.target.value);
              }}
            />
          )}
          {/* EMAIL DEL POSTULANTE */}
          {action !== 'edit' && mail !== disabled && (
            <Input
              title={t(`postulant.mail`)}
              placeholder={t(`postulant.placeholder.mail`)}
              name='email'
              type='text'
              register={register}
              formState={formState}
              validate={{
                required: vacancyRequirement.mail === vacancyRequirementType.mandatory,
                mail: true,
                validateEmail
              }}
              errorPosition='bottom'
              handlerOnBlur={(e) => emailOnBlur('email', e)}
              disabled={postulanExists.isLoading}
            />
          )}
          {/* CONFIRMAR EMAIL */}
          {action !== 'edit' && vacancyRequirement.mail !== vacancyRequirementType.disabled && (
            <Input
              title={t(`postulant.mailConfirm`)}
              placeholder={t(`postulant.placeholder.mail`)}
              name='emailConfirm'
              type='text'
              register={register}
              formState={formState}
              validate={{
                required: vacancyRequirement.mail === vacancyRequirementType.mandatory,
                mail: true
              }}
              errorPosition='bottom'
              handlerOnBlur={(e) => emailOnBlur('emailConfirm', e)}
              handlerOnPaste={notPaste}
            />
          )}
          {/* TIPO DE DOCUMENTO */}
          {vacancyRequirement.dni !== vacancyRequirementType.disabled && (
            <SelectForm
              title={t(`postulant.type_doc`)}
              id='type_doc'
              data={typesDocuments}
              control={control}
              formState={formState}
              isSearchable={false}
              validate={{ required: vacancyRequirement.dni === vacancyRequirementType.mandatory || getValues('dni') }}
              defaultValue={null}
              errorPosition='bottom'
            />
          )}
          {/* DNI DEL POSTULANTE */}
          {dni !== disabled && (
            <Input
              title={t(`postulant.dni`)}
              placeholder={t(`postulant.placeholder.dni`)}
              name='dni'
              type='text'
              register={register}
              formState={formState}
              validate={{
                required: vacancyRequirement.dni === vacancyRequirementType.mandatory || getValues('type_doc')
              }}
              errorPosition='bottom'
              handlerOnBlur={(e) => {
                setValue('dni', e.target.value);
                if (!e.target.value) unregister('type_doc');
              }}
            />
          )}
          {/* TELEFONO DEL POSTULANTE */}
          {phone !== disabled && (
            <InputPhone
              title={t(`postulant.phone`)}
              placeholder={t(`postulant.placeholder.phone`)}
              name='phone'
              prefix='prefix_phone_1'
              register={register}
              formState={formState}
              control={control}
              setValue={setValue}
              validate={{
                required: phone === mandatory,
                formatPhone: true
              }}
              errorPosition='bottom'
              prefixValue={prefixPhoneDefaultCountryCode}
            />
          )}
          {/* TIPO DE TELEFONO DEL POSTULANTE */}
          {phone !== disabled && (
            <SelectForm
              title={t(`postulant.type_phone_1`)}
              id='type_phone_1'
              data={typesPhone}
              control={control}
              formState={formState}
              isSearchable={false}
              validate={{ required: phone === mandatory }}
              defaultValue={null}
              errorPosition='bottom'
            />
          )}
          {/* TELEFONO ADICIONAL DEL POSTULANTE */}
          {phone2 !== disabled && (
            <InputPhone
              title={t(`postulant.phone_2`)}
              placeholder={t(`postulant.placeholder.phone`)}
              name='phone_2'
              prefix='prefix_phone_2'
              register={register}
              formState={formState}
              control={control}
              setValue={setValue}
              validate={{
                required: phone2 === mandatory,
                formatPhone: true
              }}
              errorPosition='bottom'
              prefixValue={prefixPhoneSecondaryCountryCode}
            />
          )}
          {/* TIPO DE TELEFONO DEL POSTULANTE ADICIONAL */}
          {phone2 !== disabled && (
            <SelectForm
              title={t(`postulant.type_phone_2`)}
              id='type_phone_2'
              data={typesPhone}
              control={control}
              formState={formState}
              isSearchable={false}
              validate={{ required: phone2 === mandatory }}
              defaultValue={null}
              errorPosition='bottom'
            />
          )}
          {/* GENERO DEL POSTULANTE */}
          {gender !== disabled && (
            <SelectForm
              title={t(`postulant.gender`)}
              id='gender'
              data={_genderCustom}
              control={control}
              formState={formState}
              isSearchable
              validate={{ required: gender === mandatory }}
              defaultValue={null}
              errorPosition='bottom'
            />
          )}
          {/* NACIONALIDAD DEL POSTULANTE */}
          {nationality !== disabled && (
            <SelectForm
              title={t(`postulant.nationality`)}
              id='nationality'
              data={countries}
              disabled={countries.length === 0}
              isLoading={_getCountries.isLoading}
              control={control}
              formState={formState}
              isSearchable
              validate={{ required: nationality === mandatory }}
              defaultValue={null}
              errorPosition='bottom'
            />
          )}
          {/* PAIS DE RESIDENCIA DEL POSTULANTE */}
          {locality !== disabled && (
            <SelectForm
              title={t(`postulant.location.country`)}
              id='location.country'
              data={countries}
              isLoading={_getCountries.isLoading}
              disabled={countries.length === 0}
              control={control}
              formState={formState}
              isSearchable
              validate={{ required: locality === mandatory }}
              onChangee={(data) => {
                filterProvincies(data.value);
              }}
              errorPosition='bottom'
            />
          )}
          {/* PROVINCIA DEL POSTULANTE */}
          {locality !== disabled && (
            <SelectForm
              title={t(`postulant.location.province`)}
              id='location.province'
              data={provinces}
              isLoading={_getProvinces.isLoading}
              disabled={provinces.length === 0}
              control={control}
              formState={formState}
              isSearchable
              validate={{
                required: locality === mandatory && !!provinces.length
              }}
              onChangee={(data) => {
                filterLocalites(data.value);
              }}
              errorPosition='bottom'
            />
          )}
          {/* LOCALIDAD DEL POSTULANTE */}
          {locality !== disabled && (
            <SelectForm
              title={t(`postulant.location.city`)}
              id='location.city'
              data={localities}
              isLoading={_getCountryLocalities.isLoading}
              disabled={localities.length === 0}
              control={control}
              formState={formState}
              isSearchable
              validate={{
                required: locality === mandatory && !!localities.length
              }}
              errorPosition='bottom'
            />
          )}
          {/* DOMICILIO DEL POSTULANTE */}
          {address !== disabled && (
            <Input
              title={t(`postulant.permanent_address`)}
              placeholder={t(`postulant.placeholder.permanent_address`)}
              name='permanent_address'
              type='text'
              register={register}
              formState={formState}
              validate={{
                required: address === mandatory
              }}
              errorPosition='bottom'
              handlerOnBlur={(e) => {
                setValue(`permanent_address`, e.target.value);
              }}
            />
          )}
          {born !== disabled && (
            <SelectDate
              allow16yearsPostulants={getDataPostulant.allow16yearsPostulants}
              clearErrors={clearErrors}
              control={control}
              date={postulant && postulant.born}
              formState={formState}
              id='bornObj'
              getValues={getValues}
              register={register}
              setValue={setValue}
              setError={setError}
              title={t('postulant.born')}
              validate={{
                required: born === mandatory,
                whiteSpace: true
              }}
              errorPosition='bottom'
            />
          )}
          {/* DISCAPACITADO */}
          {vacancy.apto_discapacitados && handicapped !== disabled && (
            <DisabledPerson
              control={control}
              formState={formState}
              register={register}
              unregister={unregister}
              setValue={setValue}
              clearErrors={clearErrors}
              postulant={postulant}
              handicapped={handicapped}
            />
          )}
          {!arrRequeriment && !hasQuestions && (
            <div className='twoColumns'>
              <PersonalExpectations
                vacancyRequirement={vacancyRequirement}
                register={register}
                formState={formState}
                control={control}
                postulant={postulant}
                action={action}
                setValue={setValue}
              />
            </div>
          )}
          {action !== 'edit' && !arrRequeriment && !hasQuestions && (
            <>
              <div className='twoColumns'>
                <Separate />
              </div>
              <div className='twoColumns'>
                <TermsConditions formState={formState} control={control} />
              </div>
            </>
          )}
        </SubContainer>
      </Container>
    );
  }
);
